import { handleCommonErrors } from 'utils/api'
import {
  InvalidUserUnauthorized,
  InvalidUserWrongAccountUsed,
} from '../useAPIKinerja'

import type {
  TPraktikKinerjaFormName,
  TResetSKPPhase,
} from 'types/kinerja-ps-pegawai'

export const basePath = '/guru/pps'

export const apiPaths = {
  basePath,
  summary: (periodeId: string) => `${basePath}/v1/pps/${periodeId}/summary`,
  status: (periodeId: string) => `${basePath}/v1/pps/${periodeId}/status`,
  rhkExists: (periodeId: string) =>
    `${basePath}/v1/pps/${periodeId}/rhk-atasan/exists`,
  perencanaan: `${basePath}/v1/pps/perencanaan`,
  perencanaanV2: `${basePath}/v2/pps/perencanaan`,
  perencanaanByPeriod: (periodeId: string) =>
    `${apiPaths.perencanaan}/${periodeId}`,
  perencanaanByPeriodV2: (periodeId: string) =>
    `${apiPaths.perencanaanV2}/${periodeId}`,
  summaryPerencanaan: (periodeId: string) =>
    `${apiPaths.perencanaanByPeriod(periodeId)}/summary`,
  getPeriod: `${basePath}/v1/pps/periode`,
  praktikKinerja: `${basePath}/v1/pps/perencanaan/praktik-kinerja`,
  praktikKinerjaV2: `${basePath}/v2/pps/perencanaan/praktik-kinerja`,
  pengembanganKompetensi: `${basePath}/v1/pps/perencanaan/pengembangan-kompetensi`,
  pengembanganKompetensiByPeriod: (periodeId: string) =>
    `${basePath}/v1/pps/perencanaan/${periodeId}/pengembangan-kompetensi`,
  pengembanganKompetensiV2: `${basePath}/v2/pps/perencanaan/pengembangan-kompetensi`,
  pengembanganKompetensiV2Aspects: (periodeId: string) =>
    `${apiPaths.perencanaanByPeriodV2(
      periodeId
    )}/pengembangan-kompetensi-activities`,
  ajukanPerencanaan: (periodeId: string) =>
    `${apiPaths.perencanaan}/${periodeId}/ajukan`,
  profile: `${basePath}/v1/pps/profile`,
  mulaiPelaksanaan: `${basePath}/v1/pps/perencanaan/mulai-pelaksanaan`,
  syncToEkin: `${basePath}/v1/pps/perencanaan/sync-to-ekin`,
  pelaksanaan: `${basePath}/v1/pps/pelaksanaan`,
  resetSkp: (periodeId: string, phase: TResetSKPPhase) =>
    `${basePath}/v1/pps/${periodeId}/skp/reset/${phase}`,
  praktikKinerjaPerencanaanSummary: (skpPsId: string) =>
    `${apiPaths.pelaksanaan}/praktik-kinerja/${skpPsId}/summary/perencanaan`,
  praktikKinerjaForm: (skpPsId: string, formName: TPraktikKinerjaFormName) =>
    `${apiPaths.pelaksanaan}/praktik-kinerja/skp-ps/${skpPsId}/form-values/${formName}`,
  dokumenAkuntabilitas: (skpPsId: string) =>
    `${basePath}/v1/pps/pelaksanaan/dokumen-akuntabilitas/skp-ps/${skpPsId}/dokumen-tersedia`,
  notification: `${basePath}/v1/pps/notification`,

  // Bukti Dukung
  buktiDukung: `${basePath}/v1/pps/bukti-dukung`,
  pelaksanaanPraktikKinerja: (skpPsId: string) =>
    `${basePath}/v1/pps/${skpPsId}/praktik-kinerja`,

  // Penetapan Predikat Kinerja
  penetapan: (periodeId: string) => `${basePath}/v1/pps/penetapan/${periodeId}`,

  // Tugas Pokok
  tugasPokok: (periodeId: string) =>
    `${apiPaths.perencanaanByPeriodV2(periodeId)}/tugas-pokok`,
} as const

export const queryKeys = {
  API_GET_PRAKTIK_KINERJA_OPTIONS: 'perencanaan-ps-praktik-kinerja',
  API_GET_SATUAN_PENDIDIKAN_OPTIONS: 'perencanaan-ps-satuan-pendidikan',
  API_GET_PENGEMBANGAN_KOMPETENSI_INDIKATOR: 'perencanaan-ps-indikator',
  API_GET_PENGEMBANGAN_KOMPETENSI_KATEGORI: 'perencanaan-ps-kategori',
  API_GET_PENGEMBANGAN_KOMPETENSI_KEGIATAN: 'perencanaan-ps-kegiatan',
  API_GET_PENGEMBANGAN_KOMPETENSI_V2_ASPECT_OPTIONS:
    'perencanaan-ps-aspect-options',
  API_GET_PERIODE: 'kinerja-ps-pegawai-periode',
  API_GET_PERIODE_DETAIL: 'kinerja-ps-pegawai-periode-detail',
  API_GET_PERENCANAAN_SUMMARY: 'perencanaan-ps-summary',
  API_GET_PERENCANAAN: 'perencanaan-ps',
  API_GET_SUMMARY: 'ps-pegawai-summary',
  API_GET_STATUS: 'ps-pegawai-status',
  API_GET_RHK_EXISTS: 'ps-pegawai-rhk-exists',
  API_GET_PROFILE: 'ps-pegawai-profile',
  API_GET_PELAKSANAAN_PERILAKU_KERJA: 'pelaksanaan-ps-perilaku-kerja',
  API_GET_PELAKSANAAN_PENGEMBANGAN_KOMPETENSI:
    'pelaksanaan-ps-pengembangan-kompetensi',
  API_GET_PRAKTIK_KINERJA_PERENCANAAN_SUMMARY:
    'pelaksanaan-ps-praktik-kinerja-perencanaan-summary',
  API_GET_PELAKSANAAN_FORM_PRAKTIK_KINERJA:
    'pelaksanaan-ps-form-praktik-kinerja',
  API_GET_PRAKTIK_KINERJA_PELAKSANAAN_SUMMARY:
    'pelaksanaan-ps-praktik-kinerja-summary',
  API_GET_PRAKTIK_KINERJA_PELAKSANAAN: 'pelaksanaan-ps-praktik-kinerja',
  API_GET_PRAKTIK_KINERJA_FORM_VALUES:
    'pelaksanaan-ps-praktik-kinerja-form-values',
  API_GET_DOKUMEN_AKUNTABILITAS: 'ps-pegawai-dokumen-akuntabilitas',
  API_GET_PRAKTIK_KINERJA_PELAKSANAAN_SURVEY_PENDAMPINGAN:
    'pelaksanaan-ps-praktik-kinerja-survey-pendampingan',
  API_GET_NOTIFICATION_LIST: 'ps-pegawai-notification-list',

  // Bukti Dukung Periodik
  API_GET_BUKTI_DUKUNG_PERIODIK_YEAR_LIST:
    'penilai-ps-bukti-dukung-periodik-year-list',
  API_GET_BUKTI_DUKUNG_PERIODIK_MONTH_LIST:
    'penilai-ps-bukti-dukung-periodik-month-list',
  API_GET_BUKTI_DUKUNG_PERIODIK_DETAIL:
    'penilai-ps-bukti-dukung-periodik-detail',

  // Bukti Dukung Tahunan
  API_GET_BUKTI_DUKUNG_TAHUNAN_YEAR_LIST:
    'penilai-ps-bukti-dukung-tahunan-year-list',
  API_GET_BUKTI_DUKUNG_TAHUNAN_LIST: 'penilai-ps-bukti-dukung-tahunan-list',
  API_GET_BUKTI_DUKUNG_TAHUNAN_DETAIL: 'penilai-ps-bukti-dukung-tahunan-detail',

  // Penetapan Predikat Kinerja
  API_GET_PENETAPAN_SUMMARY: 'penilai-ps-penetapan-summary',
}

export function handleError(status, path, error) {
  if (status === 401) {
    throw new InvalidUserUnauthorized(path, error)
  } else if (status === 403) {
    throw new InvalidUserWrongAccountUsed(path, error)
  } else {
    handleCommonErrors(status, path, error)
  }
}

export function validateStatus(status: number) {
  return status < 400
}
